import { useState } from 'react';
import { gql, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import {
    Button,
    Dropdown,
    Form,
    FormField,
    FormInput,
    Input,
    Message,
    Radio,
} from 'semantic-ui-react';

import { formatDateForBirth } from '../../../dates';
import { PATHS } from '../../../paths';
import { GENDER_OPTIONS } from '../../../utils/registration';
import { GET_TAGS_FOR_GROUP } from '../../../queries/tags';
import { CREATE_PERSON_WITH_REGISTRATION } from '../../../mutations/PERSON_REGISTRATION';
import { usePersonDocumentUpload, type PendingDocument } from '../../../hooks/usePersonDocumentUpload';
import { PersonIdPicker } from '../PersonIdPicker';

import { ConsentSection, EMPTY_CONSENT, type ConsentState } from './ConsentSection';
import { EMPTY_GUARDIAN, GuardianSection, type GuardianState } from './GuardianSection';
import { SupportingDocuments } from './SupportingDocuments';

const GET_ORG_SCRIPT_LABEL = gql`
query OrganisationScriptLabel {
  me {
    organisation {
      scriptLabel
    }
  }
}`;

const GET_UPLOAD_URLS = gql`
query PersonUploadUrls($personId: Int!) {
  user(personId: $personId) {
    id
    uploadURL
    uploadURLSmall
  }
}`;

/**
 * The registration form.
 *
 * Fields are in a **logical** order rather than the paper form's — identity, then the
 * communities someone belongs to, then what they consented to, then (for a child) who consented
 * on their behalf, then attachments. The brief explicitly released the "same order as the form"
 * constraint, and this grouping is what makes the greyed guardian section legible: it sits
 * immediately after the consents it substitutes for.
 *
 * The classic form is untouched and still reachable from the toggle above. Both call the same
 * `adminCreateUser` mutation; this one simply fills in arguments the other leaves null.
 */

/**
 * The communities someone also belongs to, beyond the one that drives their identifier.
 *
 * Per Ants: a person in Joyya Goods *and* a location community is coded as the **location**
 * community. The identifier formula already picks exactly one group value, so that rule is
 * preserved by construction — everything ticked here is an extra tag and never the ID source.
 */
const ExtraCommunities = ({
    groupId,
    excludeTagId,
    value,
    onChange,
}: {
    groupId: number | null;
    excludeTagId: number | null;
    value: number[];
    onChange: (next: number[]) => void;
}) => {
    const { data } = useQuery(GET_TAGS_FOR_GROUP, {
        variables: { groupId },
        skip: groupId == null,
    });

    const options = (data?.tagsForGroup ?? [])
        .filter((tag: any) => tag.id !== excludeTagId)
        .map((tag: any) => ({ key: tag.id, text: tag.name, value: tag.id }));

    if (groupId == null) {
        return null;
    }

    return (
        <FormField>
            <label>Also part of these communities</label>
            <Dropdown
                placeholder="Tick all that apply"
                fluid
                multiple
                search
                selection
                clearable
                options={options}
                value={value}
                onChange={(_, datum) => onChange((datum.value as number[]) ?? [])}
            />
            <div className="consent-hint">
                Someone in Joyya Goods and a location community is coded as the location
                community — that is the one above, which drives their identifier.
            </div>
        </FormField>
    );
};

const NewPersonCreator = () => {
    const navigate = useNavigate();

    // --- identity ---
    const [name, setName] = useState('');
    const [preferredName, setPreferredName] = useState('');
    const [scriptName, setScriptName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [dob, setDob] = useState<Date | null>(null);
    const [graphqlDob, setGraphqlDob] = useState('');
    const [gender, setGender] = useState('');
    const [photoFile, setPhotoFile] = useState<File | null>(null);

    // --- identifier & communities ---
    const [identifier, setIdentifier] = useState('');
    const [tagIds, setTagIds] = useState<number[]>([]);
    const [communityGroupId, setCommunityGroupId] = useState<number | null>(null);
    const [extraTagIds, setExtraTagIds] = useState<number[]>([]);

    // --- consent & guardian ---
    const [consent, setConsent] = useState<ConsentState>(EMPTY_CONSENT);
    const [guardian, setGuardian] = useState<GuardianState>(EMPTY_GUARDIAN);

    // --- documents ---
    const [documents, setDocuments] = useState<PendingDocument[]>([]);

    const [showErrors, setShowErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState('');

    const [createPersonMutation, { loading }] = useMutation(CREATE_PERSON_WITH_REGISTRATION);
    const [fetchUploadUrls] = useLazyQuery(GET_UPLOAD_URLS, { fetchPolicy: 'network-only' });
    const uploadDocuments = usePersonDocumentUpload();

    const { data: orgData } = useQuery(GET_ORG_SCRIPT_LABEL);
    const scriptLabel = orgData?.me?.organisation?.scriptLabel || 'Bengali';

    // Exactly the classic form's gate. Nothing about consent is required to save: a blank tick
    // means "not given", and the follow-up worklist is built on finding those.
    const isComplete = !!(name && address && graphqlDob && identifier);

    const uploadPhoto = async (personId: number) => {
        if (!photoFile) return;
        try {
            const { data } = await fetchUploadUrls({ variables: { personId } });
            const urls = [data?.user?.uploadURL, data?.user?.uploadURLSmall].filter(Boolean) as string[];
            await Promise.all(urls.map((url) => fetch(url, {
                method: 'PUT',
                body: photoFile,
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Length': photoFile.size.toString(),
                },
                referrer: 'no-referrer',
            })));
        } catch (photoErr) {
            // The person was created successfully; only the photo failed. Don't block navigation.
            console.error('Photo upload failed', photoErr);
        }
    };

    const handleSave = async () => {
        if (!isComplete) {
            setShowErrors(true);
            return;
        }

        setErrorMessage('');
        setWarningMessage('');

        try {
            const result = await createPersonMutation({
                variables: {
                    name,
                    preferredName,
                    address,
                    scriptName,
                    identifier,
                    phone,
                    dob: graphqlDob,
                    gender: gender || null,
                    // The identifier's community first, then anything else ticked. De-duplicated
                    // because the picker's tag can also appear in the extra list on a re-pick.
                    tagIds: Array.from(new Set([...tagIds, ...extraTagIds])),
                    picturePermission: consent.picturePermission,
                    // Only this form sends it, and it is what stamps `completedAt` — people
                    // added on the classic form deliberately get no registration row at all.
                    registrationCompleted: true,
                    consentDataAggregate: consent.consentDataAggregate,
                    consentDataReporting: consent.consentDataReporting,
                    consentDataPrograms: consent.consentDataPrograms,
                    consentDataCollection: consent.consentDataCollection,
                    consentHealthSafety: consent.consentHealthSafety,
                    idSightedByCM: consent.idSightedByCM,
                    ...guardian,
                },
            });

            const newId = result.data.adminCreateUser.id;

            await uploadPhoto(newId);

            const failed = await uploadDocuments(newId, documents);

            if (failed.length > 0) {
                // The person is saved. Say which attachments did not make it **and why**, and
                // still go to the profile — they can be re-added there.
                setWarningMessage(
                    `Saved, but these documents did not upload — ${
                        failed.map((f) => `${f.fileName}: ${f.reason}`).join(' · ')
                    }`
                );
            }

            navigate(`/people/${newId}`);
        } catch (e: any) {
            setErrorMessage(e?.message || 'Something went wrong creating this person.');
        }
    };

    return (
        <Form error={!!errorMessage} size="small">
            {/* ------------------------------------------------------------- identity */}
            <div className="creator-section">
                <div className="creator-section-title">Identity</div>

                <FormField>
                    <label>Identifier</label>
                    <PersonIdPicker
                        setId={setIdentifier}
                        setTagIds={setTagIds}
                        showErrors={showErrors}
                        onGroupIds={(ids) => setCommunityGroupId(ids[0] ?? null)}
                    />
                    <Input
                        fluid
                        readOnly
                        className="identifier-display"
                        value={identifier}
                        placeholder="(Automatically allocated)"
                    />
                </FormField>

                <Form.Group widths="equal">
                    <FormInput
                        error={showErrors && name.length < 1 && 'Please enter a name'}
                        fluid
                        label="Name"
                        placeholder="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <FormInput
                        fluid
                        label="Preferred name"
                        placeholder="Preferred name"
                        value={preferredName}
                        onChange={(e) => setPreferredName(e.target.value)}
                    />
                </Form.Group>

                <Form.Group widths="equal">
                    <FormInput
                        fluid
                        label={`Name in ${scriptLabel}`}
                        placeholder=""
                        value={scriptName}
                        onChange={(e) => setScriptName(e.target.value)}
                    />
                    <FormInput
                        fluid
                        label="Phone"
                        placeholder="Phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </Form.Group>

                <Form.Group widths="equal">
                    <FormField error={showErrors && graphqlDob.length < 1}>
                        <label>Date of birth</label>
                        <DatePicker
                            selected={dob}
                            dateFormat="dd/MM/yyyy"
                            onChange={(date) => {
                                setGraphqlDob(date != null ? formatDateForBirth(date?.getTime()) : '');
                                setDob(date);
                            }}
                            showYearDropdown
                            maxDate={new Date()}
                        />
                        {showErrors && graphqlDob.length < 1 && (
                            <div className="ui pointing above prompt label">Please enter a date of birth</div>
                        )}
                    </FormField>
                    <FormField>
                        <label>Gender</label>
                        <div className="sex-options">
                            {GENDER_OPTIONS.map((option) => (
                                <Radio
                                    key={option.value}
                                    label={option.label}
                                    name="genderRadioGroup"
                                    value={option.value}
                                    checked={gender === option.value}
                                    onChange={() => setGender(option.value)}
                                />
                            ))}
                        </div>
                    </FormField>
                </Form.Group>

                <FormInput
                    error={showErrors && address.length < 1 && 'Please enter an address'}
                    fluid
                    label="Address"
                    placeholder="Address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                />

                <FormField>
                    <label>Photo</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                    />
                </FormField>
            </div>

            {/* ---------------------------------------------------------- communities */}
            <div className="creator-section">
                <div className="creator-section-title">Communities</div>
                <ExtraCommunities
                    groupId={communityGroupId}
                    excludeTagId={tagIds[0] ?? null}
                    value={extraTagIds}
                    onChange={setExtraTagIds}
                />
            </div>

            {/* -------------------------------------------------------------- consent */}
            <ConsentSection value={consent} onChange={setConsent} />

            {/* ------------------------------------------------------------- guardian */}
            <GuardianSection value={guardian} onChange={setGuardian} personDob={dob} />

            {/* ------------------------------------------------------------ documents */}
            <SupportingDocuments documents={documents} onChange={setDocuments} disabled={loading} />

            <Message error header="Could not save" content={errorMessage} />
            {warningMessage && <Message warning visible content={warningMessage} />}

            <Button
                primary
                type="button"
                loading={loading}
                disabled={loading || !isComplete}
                onClick={() => handleSave()}
            >
                Save
            </Button>
            <Button
                type="button"
                basic
                disabled={loading}
                onClick={() => navigate(PATHS.PEOPLE)}
            >
                Cancel
            </Button>
        </Form>
    );
};

export { NewPersonCreator };
